import { Button, Input } from "@/shared/rd";
import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  changeParams,
  datesValid,
  readDates,
  readMessageSource,
  type WhatsappTab,
} from "../services/search-params";
import { LedgerFilters } from "./LedgerFilters";
import { DatePresets } from "./DatePresets";
import { SearchSelect } from "@/shared/rd";
import { WaField } from "./WaField";
import { errorText, filterForm, muted, summaryStyle } from "./styles";
const copy = keys.whatsapp;
export function Filters({
  tab,
  params,
  onChange,
}: {
  tab: WhatsappTab;
  params: URLSearchParams;
  onChange: (params: URLSearchParams) => void;
}) {
  const t = useTranslations();
  const [error, setError] = useState(false);
  const [messageIdError, setMessageIdError] = useState(false);
  const [deliveryStatus, setDeliveryStatus] = useState(
    params.get("status") ?? "",
  );
  const [templateStatus, setTemplateStatus] = useState(
    params.get("templateStatus") ?? "",
  );
  const [category, setCategory] = useState(params.get("category") ?? "");
  const dates =
    tab === "messages"
      ? {
          dateFrom: params.get("dateFrom") ?? "",
          dateTo: params.get("dateTo") ?? "",
        }
      : readDates(params);
  const filterKeys =
    tab === "templates" ? ["templateStatus", "category", "language"] : [];
  const activeCount = filterKeys.filter((key) => params.get(key)).length;
  const [expanded, setExpanded] = useState(
    tab === "messages"
      ? Boolean(dates.dateFrom || dates.dateTo)
      : activeCount > 0 ||
          (tab !== "templates" && !datesValid(dates.dateFrom, dates.dateTo)),
  );
  const actions = (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 8 }}>
      <Button type="submit" variant="primary" size="sm">
        {t(copy.apply)}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        muted
        type="button"
        onClick={() =>
          onChange(
            new URLSearchParams(
              tab === "messages" ? { tab, source: "meta" } : { tab },
            ),
          )
        }
      >
        {t(copy.reset)}
      </Button>
    </div>
  );
  if (
    tab === "usage" ||
    (tab === "messages" && readMessageSource(params) === "trioty")
  )
    return <LedgerFilters tab={tab} params={params} onChange={onChange} />;
  if (tab === "messages")
    return (
      <form
        style={filterForm}
        onSubmit={(event) => {
          event.preventDefault();
          const values = new FormData(event.currentTarget);
          const messageId = String(values.get("messageId") ?? "").trim();
          if (
            messageId &&
            !/^wamid\.[A-Za-z0-9_+/=-]{1,506}$/.test(messageId)
          ) {
            setMessageIdError(true);
            return;
          }
          setMessageIdError(false);
          const dateFrom = String(values.get("dateFrom") ?? "");
          const dateTo = String(values.get("dateTo") ?? "");
          if ((dateFrom || dateTo) && !datesValid(dateFrom, dateTo)) {
            setError(true);
            setExpanded(true);
            return;
          }
          setError(false);
          onChange(
            changeParams(params, {
              messageId,
              status: String(values.get("status") ?? ""),
              dateFrom,
              dateTo,
              message: null,
            }),
          );
        }}
      >
        {/* eslint-disable-next-line no-restricted-syntax -- Hidden form value only; SearchSelect owns the visible control. */}
        <input type="hidden" name="status" value={deliveryStatus} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", alignItems: "end", gap: 12 }}>
          <WaField
            label={t(copy.providerMessageId)}
            error={messageIdError ? t(copy.historyMessageIdError) : undefined}
          >
            {(id, errorId) => (
            <Input
              id={id}

              invalid={Boolean(errorId)}

              aria-describedby={errorId}
              name="messageId"
              dir="ltr"
              maxLength={512}
              defaultValue={params.get("messageId") ?? ""}
            />
            )}
          </WaField>
          <WaField label={t(copy.historyDeliveryUpdate)}>
            {(id) => (
            <SearchSelect
              id={id}
              label={t(copy.historyDeliveryUpdate)}
              emptyLabel={t(keys.common.noResults)}
              value={deliveryStatus}
              onChange={setDeliveryStatus}
              options={[
                { value: "", label: t(copy.all) },
                ...(
                  [
                    "sent",
                    "delivered",
                    "read",
                    "failed",
                    "deleted",
                    "unknown",
                  ] as const
                ).map((status) => ({
                  value: status,
                  label: t(copy.states[status]),
                })),
              ]}
            />
            )}
          </WaField>
          {actions}
        </div>
        <details
          open={expanded}
          onToggle={(event) => setExpanded(event.currentTarget.open)}
        >
          <summary style={summaryStyle}>
            {t(copy.customDates)}
            {dates.dateFrom && dates.dateTo && (
              <bdi style={{ marginInlineStart: 8, fontWeight: 400 }}>
                {dates.dateFrom} — {dates.dateTo} (UTC)
              </bdi>
            )}
          </summary>
          <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            <WaField label={t(copy.from)}>
              {(id) => (
              <Input
                id={id}
                name="dateFrom"
                type="date"
                defaultValue={dates.dateFrom}
              />
              )}
            </WaField>
            <WaField label={t(copy.to)}>
              {(id) => (
              <Input
                id={id}
                name="dateTo"
                type="date"
                defaultValue={dates.dateTo}
              />
              )}
            </WaField>
          </div>
        </details>
        <p style={muted}>{t(copy.historyDateHint)}</p>
        {error && (
          <p role="alert" style={errorText}>
            {t(copy.dateError)}
          </p>
        )}
      </form>
    );
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        const values = Object.fromEntries(
          new FormData(event.currentTarget).entries(),
        );
        if (
          tab !== "templates" &&
          !datesValid(String(values.dateFrom), String(values.dateTo))
        ) {
          setError(true);
          setExpanded(true);
          return;
        }
        setError(false);
        const changes: Record<string, string | null> = {};
        for (const [key, value] of Object.entries(values))
          changes[key] = String(value);
        onChange(changeParams(params, changes));
      }}
      style={filterForm}
    >
      {tab !== "templates" ? (
        <DatePresets
          dateFrom={dates.dateFrom}
          dateTo={dates.dateTo}
          onRange={(range) => onChange(changeParams(params, range))}
          onCustom={() => setExpanded((open) => !open)}
        />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", alignItems: "end", gap: 12 }}>
          <WaField label={t(copy.name)}>
            {(id) => (
            <Input
              id={id} name="name" defaultValue={params.get("name") ?? ""} />
            )}
          </WaField>
          {actions}
        </div>
      )}
      <details
        open={expanded}
        onToggle={(event) => setExpanded(event.currentTarget.open)}
      >
        <summary style={summaryStyle}>
          {t(tab === "templates" ? copy.moreFilters : copy.filters)}
          {activeCount > 0 && (
            <span style={{ marginInlineStart: 8, fontWeight: 400 }}>
              {t(copy.filterCount, { count: activeCount })}
            </span>
          )}
        </summary>
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 12 }}>
          {tab !== "templates" && <p style={muted}>{t(copy.customDates)}</p>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            {tab !== "templates" ? (
              <>
                <WaField label={t(copy.from)}>
                  {(id) => (
                  <Input
                    id={id}
                    name="dateFrom"
                    type="date"
                    required
                    defaultValue={dates.dateFrom}
                  />
                  )}
                </WaField>
                <WaField label={t(copy.to)}>
                  {(id) => (
                  <Input
                    id={id}
                    name="dateTo"
                    type="date"
                    required
                    defaultValue={dates.dateTo}
                  />
                  )}
                </WaField>
              </>
            ) : (
              <>
                {/* eslint-disable-next-line no-restricted-syntax -- Hidden form value only; SearchSelect owns the visible control. */}
                <input
                  type="hidden"
                  name="templateStatus"
                  value={templateStatus}
                />
                {/* eslint-disable-next-line no-restricted-syntax -- Hidden form value only; SearchSelect owns the visible control. */}
                <input type="hidden" name="category" value={category} />
                <WaField label={t(copy.status)}>
                  {(id) => (
                  <SearchSelect
                    id={id}
                    label={t(copy.status)}
                    emptyLabel={t(keys.common.noResults)}
                    value={templateStatus}
                    onChange={setTemplateStatus}
                    options={[
                      { value: "", label: t(copy.all) },
                      ...(
                        [
                          "APPROVED",
                          "PENDING",
                          "REJECTED",
                          "PAUSED",
                          "DISABLED",
                          "ARCHIVED",
                        ] as const
                      ).map((value) => ({
                        value,
                        label: t(copy.states[value]),
                      })),
                    ]}
                  />
                  )}
                </WaField>
                <WaField label={t(copy.category)}>
                  {(id) => (
                  <SearchSelect
                    id={id}
                    label={t(copy.category)}
                    emptyLabel={t(keys.common.noResults)}
                    value={category}
                    onChange={setCategory}
                    options={[
                      { value: "", label: t(copy.all) },
                      ...(
                        ["UTILITY", "MARKETING", "AUTHENTICATION"] as const
                      ).map((value) => ({ value, label: t(copy[value]) })),
                    ]}
                  />
                  )}
                </WaField>
                <WaField label={t(copy.language)}>
                  {(id) => (
                  <Input
                    id={id}
                    name="language"
                    dir="ltr"
                    defaultValue={params.get("language") ?? ""}
                  />
                  )}
                </WaField>
              </>
            )}
          </div>
          {error && (
            <p role="alert" style={errorText}>
              {t(copy.dateError)}
            </p>
          )}
          {tab !== "templates" && actions}
        </div>
      </details>
    </form>
  );
}
